import { useState } from "react";

// Redux/Hooks/APIs/Utils
import { RateResponse } from "@/main/models/response/rateResponse";
import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { rateApi } from "@/main/api";
import useAsyncModalConfirmReason from "@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";
import { toast } from "react-toastify";

// Components
import { IconButton, TableCell, TableRow } from "@mui/material";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import Close from "@mui/icons-material/Close";

// Styles
import * as S from "../styles";
import { formatCurrency, formatPercentage } from "@/main/services/utilsNumber";

interface IRowRate {
	index: number;
	rate: RateResponse;
	onRemove: () => void;
}
function RowRate({ index, rate, onRemove }: Readonly<IRowRate>) {
	// #region hooks
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();
	// #endregion

	// #region states
	const [isLoadingRemove, setIsLoadingRemove] = useState<boolean>(false);
	// #enregion

	// #region handlers
	async function handleRemoveRate() {
		if (isNullUndefined(rate.guidRate)) {
			return;
		}

		let resultConfirm = null;
		let filesRequest = undefined;

		resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);
		if (!resultConfirm.confirmed) return;

		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		setIsLoadingRemove(true);

		const request = {
			guidRate: rate.guidRate,
			changeJustification: {
				guidReason: resultConfirm?.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm?.observation || null,
				filesUpload: filesRequest,
			},
		};

		rateApi.deleteRate(request).then((res) => {
			setIsLoadingRemove(false);
			const resData = res.data.result;
			if (!isNullUndefined(resData)) {
				toast.success(getTranslateByKey("information.rateRemovedSucess"));
				onRemove();
			}
		});
	}
	// #endregion

	return (
		<TableRow key={`id-row-rate${index}`}>
			<TableCell>
				<div
					style={{
						maxWidth: 100,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
					title={rate.insurers?.map((item) => item.value).join(", ")}
				>
					{rate.insurers?.map((item) => item.value).join(", ") ?? getTranslateByKey("information.all")}
				</div>
			</TableCell>
			<TableCell>
				<div
					style={{
						maxWidth: 100,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
					}}
					title={rate.manufactures.map((item) => item.value).join(", ")}
				>
					{rate.manufactures.map((item) => item.value).join(", ")}
				</div>
			</TableCell>
			<TableCell>{rate.ageStart}</TableCell>
			<TableCell>{rate.ageEnd}</TableCell>
			<TableCell>{rate.affectedTypes.map((item) => item.value).join(", ")}</TableCell>
			<TableCell>{formatCurrency(rate.rateLabour, true)}</TableCell>
			<TableCell>{formatPercentage(rate.discountParts)}</TableCell>
			<TableCell>{rate.observations}</TableCell>
			<TableCell>{rate.lastUpdatedDate.concat(" - ", rate.userName)}</TableCell>
			<TableCell>{rate.status}</TableCell>
			<TableCell width="10%">
				{rate.isActive && (
					<S.ActionButtons>
						<TooltipComponent title={`${getTranslateByKey("information.deleteRate")}`}>
							<IconButton
								id={`id-rate-remove`}
								size="small"
								style={{ width: 24, height: 24, padding: 0 }}
								disabled={isLoadingRemove}
								onClick={() => {
									handleRemoveRate();
								}}
							>
								{isLoadingRemove ? <LoadingComponent size={16} /> : <Close />}
							</IconButton>
						</TooltipComponent>
					</S.ActionButtons>
				)}
			</TableCell>
			<ModalConfirmationReason closeByOverlay={false} />
		</TableRow>
	);
}

export default RowRate;
